import { ListTodo } from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import type { RoomTask } from "@/api/generated/RoomTask"
import { CodeBlock } from "@/components/app/code-block"
import { KeyValueList } from "@/components/app/key-value"
import { Markdown } from "@/components/app/markdown"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState } from "@/components/app/states"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { TASK_STATUSES, type TaskStatus, TaskStatusBadge } from "./room-status"

/** Groups tasks by status in board order (in progress first, done last). */
export function groupTasks(tasks: RoomTask[]): { status: TaskStatus; tasks: RoomTask[] }[] {
  return TASK_STATUSES.map((status) => ({ status, tasks: tasks.filter((x) => x.status === status) })).filter(
    (g) => g.tasks.length > 0,
  )
}

export function RoomTasksPanel({ tasks }: { tasks: RoomTask[] }) {
  const { t } = useTranslation()
  const [openId, setOpenId] = React.useState<number | null>(null)
  const groups = React.useMemo(() => groupTasks(tasks), [tasks])
  const open = tasks.find((x) => x.id === openId) ?? null
  if (tasks.length === 0)
    return (
      <EmptyState
        variant="plain"
        size="sm"
        icon={ListTodo}
        title={t("workplace.room.tasks.emptyTitle")}
        description={t("workplace.room.tasks.emptyDescription")}
      />
    )
  const done = tasks.filter((x) => x.status === "done").length
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <div className="text-muted-foreground flex justify-between text-xs">
          <span>{t("workplace.room.tasks.progress", { done, total: tasks.length })}</span>
        </div>
        <div className="bg-muted h-1.5 overflow-hidden rounded-full">
          <div
            className="bg-success h-full transition-[width]"
            style={{ width: `${(done / tasks.length) * 100}%` }}
          />
        </div>
      </div>
      {groups.map((g) => (
        <section key={g.status} className="space-y-1.5">
          <h3 className="text-muted-foreground flex items-center gap-1.5 text-[0.6875rem] font-medium tracking-wide uppercase">
            {t(`workplace.taskStatus.${g.status}`)}
            <span className="tabular-nums">{g.tasks.length}</span>
          </h3>
          <ul className="space-y-1.5">
            {g.tasks.map((task) => (
              <li key={task.id}>
                <button
                  type="button"
                  onClick={() => setOpenId(task.id)}
                  className={cn(
                    "hover:bg-muted/60 w-full rounded-lg border px-3 py-2 text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                    task.status === "done" && "opacity-75",
                  )}
                  data-testid="room-task"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="min-w-0 text-[0.8125rem] font-medium break-words">
                      <span className="text-muted-foreground mr-1 font-mono text-[0.6875rem]">
                        #{task.id}
                      </span>
                      {task.title}
                    </span>
                    <TaskStatusBadge status={task.status} />
                  </div>
                  <div className="text-muted-foreground mt-1 flex items-center gap-1 text-[0.6875rem]">
                    <span className="truncate">
                      {task.assignee_worker_name ?? t("workplace.room.tasks.unassigned")}
                    </span>
                    <span aria-hidden>·</span>
                    <RelativeTime value={task.updated_at} />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <TaskDialog task={open} onClose={() => setOpenId(null)} />
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h4 className="text-muted-foreground text-xs font-medium">{title}</h4>
      {children}
    </section>
  )
}

function TaskDialog({ task, onClose }: { task: RoomTask | null; onClose: () => void }) {
  const { t } = useTranslation()
  return (
    <Dialog open={!!task} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        {task && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 pr-6">
                <span className="text-muted-foreground font-mono text-sm">#{task.id}</span>
                <span className="min-w-0 break-words">{task.title}</span>
              </DialogTitle>
              <DialogDescription render={<div />} className="flex items-center gap-2">
                <TaskStatusBadge status={task.status} />
                {task.assignee_worker_name ?? t("workplace.room.tasks.unassigned")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5">
              <KeyValueList
                items={[
                  {
                    label: t("workplace.room.tasks.created"),
                    value: <RelativeTime value={task.created_at} />,
                  },
                  {
                    label: t("workplace.room.tasks.started"),
                    value: task.started_at ? <RelativeTime value={task.started_at} /> : null,
                    hidden: !task.started_at,
                  },
                  {
                    label: t("workplace.room.tasks.completed"),
                    value: task.completed_at ? <RelativeTime value={task.completed_at} /> : null,
                    hidden: !task.completed_at,
                  },
                ]}
              />
              {task.description && (
                <Section title={t("workplace.room.tasks.description")}>
                  <Markdown size="sm">{task.description}</Markdown>
                </Section>
              )}
              {task.instructions && (
                <Section title={t("workplace.room.tasks.instructions")}>
                  <Markdown size="sm">{task.instructions}</Markdown>
                </Section>
              )}
              {task.expected_output && (
                <Section title={t("workplace.room.tasks.expectedOutput")}>
                  <Markdown size="sm">{task.expected_output}</Markdown>
                </Section>
              )}
              {task.error && (
                <Section title={t("workplace.room.tasks.error")}>
                  <p className="text-destructive text-sm break-words whitespace-pre-wrap">{task.error}</p>
                </Section>
              )}
              {task.result_summary && (
                <Section title={t("workplace.room.tasks.resultSummary")}>
                  <Markdown size="sm">{task.result_summary}</Markdown>
                </Section>
              )}
              {task.result != null && (
                <Section title={t("workplace.room.tasks.result")}>
                  <CodeBlock
                    code={
                      typeof task.result === "string" ? task.result : JSON.stringify(task.result, null, 2)
                    }
                    wrap
                    maxHeight={320}
                  />
                </Section>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
